/**
 * Dashboard = what needs doing. An inbox of actionable items (filtered by
 * permission), the user's own day, quick actions, global search, and a
 * compact finance summary for finance users.
 */
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type {
  ActionItemDto,
  DashboardResponse,
  SearchResult,
} from "@offertli/shared";
import { Button } from "../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { apiGet } from "../lib/api.js";
import { useMe, usePermissions } from "../lib/auth.js";
import { useDebounced } from "../components/ui/controls.js";
import { cx } from "../components/ui/cx.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { ErrorPanel, LoadingPanel, formatMoney } from "../features/shared.js";

const SEARCH_ICONS: Record<SearchResult["type"], string> = {
  customer: "pi pi-user",
  request: "pi pi-inbox",
  offer: "pi pi-file",
  project: "pi pi-briefcase",
  invoice: "pi pi-wallet",
  employee: "pi pi-id-card",
  catalog: "pi pi-tag",
};

function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const debounced = useDebounced(query.trim(), 250);
  const root = useRef<HTMLDivElement>(null);
  const results = useQuery({
    queryKey: ["search", debounced],
    queryFn: () =>
      apiGet<{ data: SearchResult[] }>("/search", {
        query: { q: debounced },
      }).then((response) => response.data),
    enabled: debounced.length >= 2,
  });
  const items = results.data ?? [];
  useEffect(() => setActive(0), [debounced]);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={root} className="relative">
      <i
        className="pi pi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-app-muted"
        aria-hidden
      />
      <InputText
        type="search"
        role="combobox"
        aria-expanded={open && items.length > 0}
        aria-controls="global-search-results"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive((index) => Math.min(index + 1, items.length - 1));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if (event.key === "Enter" && items[active]) {
            navigate(items[active]!.href);
          } else if (event.key === "Escape") setOpen(false);
        }}
        placeholder={t("dashboard.searchPlaceholder")}
        aria-label={t("dashboard.searchPlaceholder")}
        className="w-full py-3"
        style={{ paddingLeft: "2.5rem" }}
      />
      {open && debounced.length >= 2 ? (
        <ul
          id="global-search-results"
          role="listbox"
          className="absolute z-30 mt-1 max-h-96 w-full overflow-auto rounded-xl border border-app-border bg-app-surface py-1 shadow-lg"
        >
          {results.isLoading ? (
            <li className="px-4 py-3 text-sm text-app-muted">
              {t("common.loading")}
            </li>
          ) : items.length === 0 ? (
            <li className="px-4 py-3 text-sm text-app-muted">
              {t("common.noMatches")}
            </li>
          ) : (
            items.map((item, index) => (
              <li
                key={`${item.type}-${item.id}`}
                role="option"
                aria-selected={index === active}
              >
                <Link
                  to={item.href}
                  onMouseEnter={() => setActive(index)}
                  className={cx(
                    "flex items-center gap-3 px-4 py-2",
                    index === active && "bg-app-background",
                  )}
                >
                  <i
                    className={cx(SEARCH_ICONS[item.type], "text-app-muted")}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {item.label}
                    </div>
                    <div className="truncate text-xs text-app-muted">
                      {t(`search.types.${item.type}`)}
                      {item.number ? ` · ${item.number}` : ""}
                      {item.sublabel ? ` · ${item.sublabel}` : ""}
                    </div>
                  </div>
                  {item.status ? <StatusBadge status={item.status} /> : null}
                </Link>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

function InboxList({ items }: { items: ActionItemDto[] }) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-app-border bg-app-surface px-4 py-6 text-sm text-app-muted">
        <i className="pi pi-check-circle text-xl text-success" aria-hidden />
        {t("inbox.allDone")}
      </div>
    );
  }
  return (
    <ul className="overflow-hidden rounded-xl border border-app-border bg-app-surface">
      {items.map((item) => (
        <li
          key={item.code}
          className="border-b border-app-border last:border-0"
        >
          <Link
            to={item.href ?? "/"}
            className="flex items-center gap-3 px-4 py-3 hover:bg-app-background"
          >
            <span
              className={cx(
                "h-8 w-1 shrink-0 rounded-full",
                item.priority === "high"
                  ? "bg-danger"
                  : item.priority === "normal"
                    ? "bg-brand"
                    : "bg-app-border",
              )}
              aria-hidden
            />
            <span className="min-w-0 flex-1 text-sm">
              {t(`inbox.items.${item.code}`, { count: item.count })}
            </span>
            <span className="rounded-full bg-app-background px-2.5 py-0.5 text-sm font-semibold tabular-nums">
              {item.count}
            </span>
            <i
              className="pi pi-chevron-right text-xs text-app-muted"
              aria-hidden
            />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function MyDay({ day }: { day: DashboardResponse["myDay"] }) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const time = (value: string) =>
    new Intl.DateTimeFormat(undefined, {
      timeStyle: "short",
      timeZone: day.timeZone,
    }).format(new Date(value));
  return (
    <section className="rounded-xl border border-app-border bg-app-surface p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{t("dashboard.myDay")}</h2>
        {has("time.record") ? (
          <Link
            to="/time/clock"
            className={cx(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
              day.clockedIn
                ? "bg-success-soft text-success"
                : "bg-app-background text-app-muted",
            )}
          >
            <span
              className={cx(
                "h-2 w-2 rounded-full",
                day.clockedIn ? "bg-success" : "bg-app-muted",
              )}
              aria-hidden
            />
            {day.clockedIn ? t("dashboard.clockedIn") : t("dashboard.clockIn")}
          </Link>
        ) : null}
      </div>
      {day.assignments.length === 0 ? (
        <p className="text-sm text-app-muted">{t("dashboard.noAssignments")}</p>
      ) : (
        <ul className="space-y-2">
          {day.assignments.map((assignment) => (
            <li key={assignment.id}>
              <Link
                to={`/projects/${assignment.projectId}`}
                className="flex gap-3 rounded-lg border border-app-border p-3 hover:border-brand"
              >
                <div className="w-16 shrink-0 text-sm font-semibold tabular-nums">
                  {time(assignment.startAt)}
                  <div className="text-xs font-normal text-app-muted">
                    {time(assignment.endAt)}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {assignment.projectTitle}
                  </div>
                  <div className="truncate text-xs text-app-muted">
                    {assignment.customerName} · {assignment.projectNumber}
                  </div>
                  {assignment.notes ? (
                    <div className="mt-1 text-xs text-app-muted">
                      {assignment.notes}
                    </div>
                  ) : null}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FinanceSummary({
  finance,
}: {
  finance: NonNullable<DashboardResponse["finance"]>;
}) {
  const { t, i18n } = useTranslation();
  const max = Math.max(
    1,
    ...finance.months.flatMap((month) => [
      Number(month.invoiced),
      Number(month.paid),
    ]),
  );
  const monthLabel = (month: string) =>
    new Intl.DateTimeFormat(i18n.resolvedLanguage, { month: "short" }).format(
      new Date(`${month}-01T12:00:00`),
    );
  const tiles: Array<{
    label: string;
    value: string;
    to: string;
    tone?: "danger";
    note?: string;
  }> = [
    {
      label: t("dashboard.outstanding"),
      value: formatMoney(finance.outstanding, finance.currency),
      to: "/invoices?tab=open",
    },
    {
      label: t("dashboard.overdue"),
      value: formatMoney(finance.overdue, finance.currency),
      to: "/invoices?tab=overdue",
      tone: finance.overdueCount > 0 ? "danger" : undefined,
      note: t("dashboard.invoiceCount", { count: finance.overdueCount }),
    },
    {
      label: t("dashboard.invoicedThisMonth"),
      value: formatMoney(finance.invoicedThisMonth, finance.currency),
      to: "/invoices",
    },
    {
      label: t("dashboard.openOffers"),
      value: formatMoney(finance.openOffersValue, finance.currency),
      to: "/offers?status=sent",
    },
  ];
  return (
    <section className="space-y-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((tile) => (
          <Link
            key={tile.label}
            to={tile.to}
            className="rounded-xl border border-app-border bg-app-surface p-4 hover:border-brand"
          >
            <div className="text-xs font-medium uppercase tracking-wide text-app-muted">
              {tile.label}
            </div>
            <div
              className={cx(
                "mt-1 text-lg font-semibold tabular-nums sm:text-xl",
                tile.tone === "danger" && "text-danger",
              )}
            >
              {tile.value}
            </div>
            {tile.note ? (
              <div className="text-xs text-app-muted">{tile.note}</div>
            ) : null}
          </Link>
        ))}
      </div>
      <div className="rounded-xl border border-app-border bg-app-surface p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">{t("dashboard.cashflow")}</h2>
          <div className="flex gap-4 text-xs text-app-muted">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-brand" aria-hidden />
              {t("dashboard.invoiced")}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-success" aria-hidden />
              {t("dashboard.paid")}
            </span>
          </div>
        </div>
        <div
          className="flex h-36 items-end gap-2 sm:gap-4"
          role="img"
          aria-label={finance.months
            .map(
              (month) =>
                `${monthLabel(month.month)}: ${t("dashboard.invoiced")} ${formatMoney(month.invoiced, finance.currency)}, ${t("dashboard.paid")} ${formatMoney(month.paid, finance.currency)}`,
            )
            .join("; ")}
        >
          {finance.months.map((month) => (
            <div
              key={month.month}
              className="flex flex-1 flex-col items-center gap-1"
            >
              <div className="flex h-28 w-full items-end justify-center gap-1">
                <div
                  className="w-1/3 max-w-5 rounded-t bg-brand"
                  style={{ height: `${(Number(month.invoiced) / max) * 100}%` }}
                  title={formatMoney(month.invoiced, finance.currency)}
                />
                <div
                  className="w-1/3 max-w-5 rounded-t bg-success"
                  style={{ height: `${(Number(month.paid) / max) * 100}%` }}
                  title={formatMoney(month.paid, finance.currency)}
                />
              </div>
              <span className="text-xs text-app-muted">
                {monthLabel(month.month)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { has } = usePermissions();
  const dashboard = useQuery({
    queryKey: ["dashboard"],
    queryFn: () =>
      apiGet<{ data: DashboardResponse }>("/dashboard").then(
        (response) => response.data,
      ),
    refetchInterval: 120_000,
  });
  const hour = new Date().getHours();
  const greeting =
    hour < 11
      ? t("dashboard.goodMorning")
      : hour < 18
        ? t("dashboard.goodDay")
        : t("dashboard.goodEvening");
  const quick = [
    has("requests.create")
      ? { to: "/requests/new", icon: "pi pi-inbox", label: t("request.new") }
      : null,
    has("customers.create")
      ? {
          to: "/customers/new",
          icon: "pi pi-user-plus",
          label: t("customer.new"),
        }
      : null,
    has("offers.create") && has("finance.view")
      ? { to: "/offers/new", icon: "pi pi-file", label: t("offer.new") }
      : null,
    has("invoices.create") && has("finance.view")
      ? { to: "/invoices/new", icon: "pi pi-wallet", label: t("invoice.new") }
      : null,
  ].filter((item): item is { to: string; icon: string; label: string } =>
    Boolean(item),
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
          {greeting}
          {me?.user.firstName ? `, ${me.user.firstName}` : ""}
        </h1>
        <p className="mt-1 text-sm text-app-muted">{t("dashboard.subtitle")}</p>
      </div>
      <GlobalSearch />
      {quick.length ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {quick.map((action) => (
            <Button
              key={action.to}
              as={Link}
              to={action.to}
              variant="outlined"
              icon={action.icon}
              className="shrink-0"
            >
              {action.label}
            </Button>
          ))}
        </div>
      ) : null}
      {dashboard.isLoading ? (
        <LoadingPanel />
      ) : dashboard.error || !dashboard.data ? (
        <ErrorPanel error={dashboard.error} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <section>
              <h2 className="mb-2 text-sm font-semibold">{t("inbox.title")}</h2>
              <InboxList items={dashboard.data.inbox} />
            </section>
            <MyDay day={dashboard.data.myDay} />
          </div>
          {dashboard.data.finance ? (
            <FinanceSummary finance={dashboard.data.finance} />
          ) : null}
        </>
      )}
    </div>
  );
}
